import { EmploymentSector } from '../../core/enum/employment-sector.enum';
import { PaymentMethod } from '../../core/models/payment/payment-method';
import { UserProfile } from '../../core/models/user/user-profile';

// Derived per-section status for the profile review-and-attest screen.
// Pure function — no DOM, no Angular. This is the high-value test seam.
export type ReviewAttestSection =
  | 'identity'
  | 'contact'
  | 'employmentIncome'
  | 'bank';
// 'reverify' is an overlay state the component applies to the identity section
// after a name/IC change is staged this session (backend holds it pending in
// ReKYC). computeSectionStatuses never emits it — it's not a data-completeness
// state — but it must not block the final confirm (Confirm hands off to re-KYC).
export type SectionStatus = 'ok' | 'needsChange' | 'missing' | 'reverify';

// Identity, contact, and employment & income are always required because each
// holds SC-mandatory KYC fields: identity (name / IC / DOB), contact (address
// etc.), and employment & income (employment sector, occupation/employment
// status, and transaction purpose). A missing one must block the final confirm
// regardless of the entry-point `required` param. Bank is the only
// context-driven section — it becomes required only when the withdraw gate adds
// it via the `required` param.
const ALWAYS_REQUIRED_SECTIONS: ReviewAttestSection[] = ['identity', 'contact', 'employmentIncome'];

const VALID_EMPLOYMENT_SECTORS: number[] = Object.values(EmploymentSector).filter(
  (value): value is number => typeof value === 'number',
);

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function isSectionComplete(section: ReviewAttestSection, profile: UserProfile, paymentMethod: PaymentMethod | null): boolean {
  switch (section) {
    case 'identity':
      return (
        !isBlank(profile?.fullName) &&
        !isBlank(profile?.identityNo) &&
        !isBlank(profile?.userIdentityNoType) &&
        !isBlank(profile?.birthday)
      );
    case 'contact':
      return (
        !isBlank(profile?.phoneNumber) &&
        !isBlank(profile?.address) &&
        !isBlank(profile?.postcode) &&
        !isBlank(profile?.city) &&
        !isBlank(profile?.countryId)
      );
    case 'employmentIncome':
      // Complete only when a defined EmploymentSector enum value is set
      // (NotApplicable = 0 counts — it's a real, server-accepted value), the
      // occupation/employment status (occupationType) is set — with its
      // "Other" free-text follow-up filled when occupationType === 255 — AND
      // transactionPurpose is set (INC-317). Income range is editable but does
      // not gate completeness.
      return (
        profile?.employmentSector != null &&
        VALID_EMPLOYMENT_SECTORS.includes(profile.employmentSector) &&
        profile?.occupationType != null &&
        (profile.occupationType !== 255 || !isBlank(profile?.othersOccupationType)) &&
        !isBlank(profile?.transactionPurpose)
      );
    case 'bank':
      return (
        !isBlank(paymentMethod?.accountHolder) &&
        !isBlank(paymentMethod?.accountNumber) &&
        !isBlank(paymentMethod?.bankId)
      );
  }
}

// Status per section — complete sections are always 'ok'; incomplete sections
// are 'missing' when the caller marked them required (blocks confirmation),
// otherwise 'needsChange' (informational, doesn't block confirmation).
export function computeSectionStatuses(
  profile: UserProfile,
  paymentMethod: PaymentMethod | null,
  requiredSections: ReviewAttestSection[],
): Record<ReviewAttestSection, SectionStatus> {
  const sections: ReviewAttestSection[] = ['identity', 'contact', 'employmentIncome', 'bank'];
  const effectiveRequired = [...requiredSections, ...ALWAYS_REQUIRED_SECTIONS];
  const statuses = {} as Record<ReviewAttestSection, SectionStatus>;

  for (const section of sections) {
    if (isSectionComplete(section, profile, paymentMethod)) {
      statuses[section] = 'ok';
    } else if (effectiveRequired.includes(section)) {
      statuses[section] = 'missing';
    } else {
      statuses[section] = 'needsChange';
    }
  }

  return statuses;
}

export function canConfirmEverythingCorrect(statuses: Record<ReviewAttestSection, SectionStatus>): boolean {
  return Object.values(statuses).every((status) => status !== 'missing');
}

// True only when a section has NO data at all — distinct from "incomplete"
// (isSectionComplete === false covers both "nothing yet" and "some fields
// filled, some missing"). The API's profile/payment-method GETs return a
// real object with blank fields rather than null when nothing is on file
// yet, so a template can't tell "empty" from "partially filled" by checking
// object truthiness alone — this is what should gate the friendly
// "nothing on file yet" placeholder instead.
export function isSectionEmpty(
  section: ReviewAttestSection,
  profile: UserProfile,
  paymentMethod: PaymentMethod | null,
): boolean {
  switch (section) {
    case 'identity':
      return (
        isBlank(profile?.fullName) &&
        isBlank(profile?.identityNo) &&
        isBlank(profile?.userIdentityNoType) &&
        isBlank(profile?.birthday)
      );
    case 'contact':
      return (
        isBlank(profile?.phoneNumber) &&
        isBlank(profile?.address) &&
        isBlank(profile?.postcode) &&
        isBlank(profile?.city) &&
        isBlank(profile?.countryId)
      );
    case 'employmentIncome':
      // Empty only when the employment sector, occupation/employment status
      // (occupationType) and transactionPurpose are ALL blank — income range
      // does not affect the empty-state placeholder (INC-317).
      return (
        profile?.employmentSector == null &&
        profile?.occupationType == null &&
        isBlank(profile?.transactionPurpose)
      );
    case 'bank':
      return (
        isBlank(paymentMethod?.accountHolder) &&
        isBlank(paymentMethod?.accountNumber) &&
        isBlank(paymentMethod?.bankId)
      );
  }
}
